export interface RGB {
  r: number;
  g: number;
  b: number;
}

export const parseToRgb = (color: string): RGB | null => {
  if (!color) return null;
  const trimmed = color.trim().toLowerCase();

  // Handle Hex
  if (trimmed.startsWith("#")) {
    const hex = trimmed.substring(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
      };
    }
    if (hex.length === 6 || hex.length === 8) {
      return {
        r: parseInt(hex.substring(0, 2), 16),
        g: parseInt(hex.substring(2, 4), 16),
        b: parseInt(hex.substring(4, 6), 16),
      };
    }
  }

  // Handle rgb / rgba
  const rgbMatch = trimmed.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
    };
  }

  // Fallback for hex values without a hash
  if (/^[0-9a-f]{3}$/.test(trimmed)) {
    return {
      r: parseInt(trimmed[0] + trimmed[0], 16),
      g: parseInt(trimmed[1] + trimmed[1], 16),
      b: parseInt(trimmed[2] + trimmed[2], 16),
    };
  }
  if (/^[0-9a-f]{6}$/.test(trimmed)) {
    return {
      r: parseInt(trimmed.substring(0, 2), 16),
      g: parseInt(trimmed.substring(2, 4), 16),
      b: parseInt(trimmed.substring(4, 6), 16),
    };
  }

  return null;
};

export const getLuminance = (r: number, g: number, b: number): number => {
  const [aR, aG, aB] = [r, g, b].map(v => {
    const sRGB = v / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * aR + 0.7152 * aG + 0.0722 * aB;
};

export const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = parseToRgb(color1);
  const rgb2 = parseToRgb(color2);
  if (!rgb1 || !rgb2) return 1;

  const l1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

export const ensureHex = (color: string | undefined | null, fallback: string): string => {
  if (!color) return fallback;
  const trimmed = color.trim();
  if (trimmed.startsWith("#") || trimmed.startsWith("rgb") || trimmed.startsWith("var") || trimmed.startsWith("hsl")) {
    return trimmed;
  }
  if (/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$|^[0-9a-fA-F]{8}$/.test(trimmed)) {
    return `#${trimmed}`;
  }
  return trimmed;
};

export interface SmartAccent {
  smartAccentColor: string;
  smartAccentTextColor: string;
}

export const getSmartAccentColors = (
  backgroundColor: string,
  primaryCtaColor: string,
  primaryCtaTextColor: string
): SmartAccent => {
  const bg = backgroundColor || "#111827";
  const cta = primaryCtaColor || "#3B82F6";
  const ctaText = primaryCtaTextColor || "#ffffff";

  const ratioCtaBg = getContrastRatio(bg, cta);
  const ratioCtaTextBg = getContrastRatio(bg, ctaText);

  // Use ctaText for progress only if the CTA itself has poor contrast AND ctaText has better contrast against bg
  const useCtaTextForProgress = ratioCtaBg < 3.0 && ratioCtaTextBg > ratioCtaBg;

  return {
    smartAccentColor: useCtaTextForProgress ? ctaText : cta,
    smartAccentTextColor: useCtaTextForProgress ? cta : ctaText,
  };
};
